/**
 * TagManager — CRUD tags pe tenant curent în Settings.
 * Afișează lista existentă, permite create cu name+color, delete.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { tagsApi, type TagColor } from '@/api/tags';
import { errorMessage } from '@/api/client';
import TagPill from '@/components/ui/TagPill';
import { confirmDialog } from '@/stores/dialog';

// Eticheta afișată vine din i18n: `admin_ui.colors.<value>`.
const COLORS: TagColor[] = [
  'mint',
  'cyan',
  'blue',
  'purple',
  'amber',
  'rose',
  'yellow',
  'indigo',
  'neutral',
];

export default function TagManager({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['tags', tenantId],
    queryFn: () => tagsApi.list(tenantId),
  });

  const [name, setName] = useState('');
  const [color, setColor] = useState<TagColor>('mint');
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => tagsApi.create(tenantId, { name: name.trim(), color }),
    onSuccess: () => {
      setName('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['tags', tenantId] });
    },
    onError: (err) => {
      setError(errorMessage(err, t('tags_section.create_error')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (tagId: string) => tagsApi.delete(tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', tenantId] });
    },
  });

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          createMutation.mutate();
        }}
        className="flex flex-wrap gap-2 items-end"
      >
        <div className="flex-1 min-w-[180px]">
          <label htmlFor="tag-manager-1"
            className="block text-[10px] uppercase font-mono mb-1"
            style={{
              color: 'var(--text-muted)',
              letterSpacing: '0.14em',
            }}
          >
            {t('tags_section.name_label')}
          </label>
          <input id="tag-manager-1"
            type="text"
            className="input"
            placeholder={t('tags_section.name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={32}
            disabled={createMutation.isPending}
          />
        </div>
        <div>
          <label htmlFor="tag-manager-2"
            className="block text-[10px] uppercase font-mono mb-1"
            style={{
              color: 'var(--text-muted)',
              letterSpacing: '0.14em',
            }}
          >
            {t('tags_section.color_label')}
          </label>
          <select id="tag-manager-2"
            className="input"
            value={color}
            onChange={(e) => setColor(e.target.value as TagColor)}
            disabled={createMutation.isPending}
          >
            {COLORS.map((c) => (
              <option key={c} value={c}>
                {t(`admin_ui.colors.${c}`)}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={createMutation.isPending || !name.trim()}
        >
          {createMutation.isPending ? t('tags_section.creating') : t('tags_section.create_tag')}
        </button>
      </form>

      {error && (
        <div
          className="text-sm rounded px-3 py-2"
          style={{
            color: 'var(--danger)',
            background: 'var(--danger-bg)',
            border: '1px solid var(--danger)',
          }}
        >
          {error}
        </div>
      )}

      <div>
        {list.isLoading && (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {t('common.loading')}
          </p>
        )}
        {list.data && list.data.length === 0 && (
          <p
            className="text-sm text-center py-4"
            style={{ color: 'var(--text-muted)' }}
          >
            {t('tags_section.none_created')}
          </p>
        )}
        {list.data && list.data.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {list.data.map((tag) => (
              <TagPill
                key={tag.id}
                label={`${tag.name} · ${tag.usage_count}`}
                color={tag.color ?? 'neutral'}
                onRemove={async () => {
                  const confirmMsg =
                    tag.usage_count > 0
                      ? t('tags_section.delete_confirm_usage', {
                          name: tag.name,
                          count: tag.usage_count,
                        })
                      : t('tags_section.delete_confirm', { name: tag.name });
                  if (await confirmDialog(confirmMsg)) {
                    deleteMutation.mutate(tag.id);
                  }
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
